import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './CoachCheckoutModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function SubscribeForm({ serviceId, serviceName, subscriptionId, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    const piOk =
      paymentIntent &&
      ['succeeded', 'processing', 'requires_capture'].includes(paymentIntent.status);

    if (piOk || subscriptionId) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/coach-services/${serviceId}/complete-subscription`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscriptionId: subscriptionId || undefined,
            paymentIntentId: paymentIntent?.id || undefined,
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to complete subscription');
        onSuccess(data);
      } catch (err) {
        setError(err.message);
      }
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <PaymentElement />
      {error && <p className="checkout-error">{error}</p>}
      <div className="checkout-actions">
        <button type="button" className="checkout-cancel" onClick={onClose} disabled={processing}>Cancel</button>
        <button type="submit" className="checkout-pay" disabled={!stripe || processing}>
          {processing ? 'Processing...' : `Subscribe to ${serviceName}`}
        </button>
      </div>
    </form>
  );
}

export default function CoachSubscribeModal({ service, coachSlug, onSuccess, onClose }) {
  const [clientSecret, setClientSecret] = useState('');
  const [stripeAccountId, setStripeAccountId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const calledRef = React.useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const initSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/coach-services/${service._id}/subscribe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to start subscription');

        // Subscription auto-activated (e.g. stored payment method) — skip payment form
        if (data.completed) {
          onSuccess(data);
          return;
        }

        setClientSecret(data.clientSecret);
        setStripeAccountId(data.stripeAccountId);
        if (data.subscriptionId) setSubscriptionId(data.subscriptionId);

        if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && data.stripeAccountId) {
          const sp = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
            stripeAccount: data.stripeAccountId
          });
          setStripePromise(sp);
        }
      } catch (err) {
        setLoadError(err.message);
      }
    };
    initSubscription();
  }, [service._id, onSuccess]);

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <div className="checkout-modal" onClick={e => e.stopPropagation()}>
        <h3>Subscribe — {service.name}</h3>
        <p className="checkout-amount">
          ${(service.price / 100).toFixed(2)}
          {service.billingInterval === 'monthly' ? '/mo' : ' one-time'}
        </p>

        {loadError && <p className="checkout-error">{loadError}</p>}

        {clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <SubscribeForm
              serviceId={service._id}
              serviceName={service.name}
              subscriptionId={subscriptionId}
              onSuccess={onSuccess}
              onClose={onClose}
            />
          </Elements>
        ) : !loadError ? (
          <p className="checkout-loading">Setting up payment...</p>
        ) : null}
      </div>
    </div>
  );
}
